import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, withCtx as _withCtx, createSlots as _createSlots, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "transfer-form" }
const _hoisted_2 = { class: "transfer-form__section transfer-form__section--from" }
const _hoisted_3 = { class: "transfer-form__section transfer-form__section--amount" }
const _hoisted_4 = { class: "transfer-form__section transfer-form__section--to" }
const _hoisted_5 = { class: "transfer-form__sub-text" }

import { BigNumber } from 'bignumber.js';
import { nanoid } from 'nanoid';
import { computed, ref, watch, type PropType } from 'vue';

import getBalance from '@exchange/helpers/balance';
import type { BalanceModel } from '@exchange/libs/balances/service/src';
import { subaccountMarginService } from '@exchange/libs/futures/service/src';
import { CURRENCY_ELIGIBLE_FOR_MARGIN } from '@exchange/libs/futures/service/src/lib/constants.config';
import { ModalError, ModalFooter } from '@exchange/libs/modals/src';
import type { ModalVariant } from '@exchange/libs/modals/src';
import { type Subaccount, SubaccountType } from '@exchange/libs/trading-accounts/service/src';
import { AmountInput, TransactionPaymentContent, useCoverForBackend } from '@exchange/libs/transactions/shared/src';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import type { CurrencyModel } from '@exchange/libs/utils/currency/src';

import SubaccountSelect, { type SubaccountOption } from './SubaccountSelect.vue';

export interface TransferFundsResponse {
  error: string | unknown | undefined;
}

export type TransferFundsCall = (data: {
  amount: string;
  currencySymbol: string;
  sourceId: string;
  sourceName: string;
  sourceType: SubaccountType;
  destinationId: string;
  destinationName: string;
  destinationType: SubaccountType;
}) => Promise<TransferFundsResponse>;

type SubaccountEffectiveCorrespondingBalanceBase = {
  available: BigNumber;
  locked: BigNumber;
};

type SubaccountEffectiveCorrespondingBalancePerps = SubaccountEffectiveCorrespondingBalanceBase & {
  used: BigNumber;
};

type SubaccountEffectiveCorrespondingBalance = SubaccountEffectiveCorrespondingBalanceBase | SubaccountEffectiveCorrespondingBalancePerps | BalanceModel;


export default /*#__PURE__*/_defineComponent({
  __name: 'TransferForm',
  props: {
  accounts: { type: Array as PropType<Array<Subaccount>>, required: true },
  currency: { type: Object as PropType<CurrencyModel | { symbol: string }>, required: true },
  preselectedFrom: { type: String, default: '' },
  preselectedTo: { type: String, required: false },
  shouldDisableSelect: { type: Boolean, required: true },
  transfer: { type: Function as PropType<TransferFundsCall>, required: true },
  variant: { type: String as PropType<ModalVariant>, required: true },
},
  emits: ["cancel"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;
const { loadingPendingTransactions, pendingAmount, warningMessage } = useCoverForBackend({ id: 'id' in props.currency ? props.currency.id : props.currency.symbol });

const preselection = props.preselectedFrom || CONSTANTS.MAIN_SUBACCOUNT_NAME;

const isMain = (v?: { name: string }) => v && v.name === CONSTANTS.MAIN_SUBACCOUNT_NAME;
const isInstant = (v?: { name: string }) => v && v.name === CONSTANTS.INSTANT_TRADE_ACCOUNT_NAME;

const amountValue = ref('');
const amountIsValid = ref(false);
const transferIsLoading = ref(false);
const updateAmountIsValid = (invalid: boolean) => {
  amountIsValid.value = !invalid;
};

const transactionPrecision = computed(() => ('transactionPrecision' in props.currency ? props.currency.transactionPrecision : 0));

const subaccountOptions = computed<Array<SubaccountOption>>(() =>
  props.accounts.map((subaccount) => {
    const correspondingBalance = subaccount.balances.find((balance) => balance.currencyCode === props.currency.symbol);
    let effectiveCorrespondingBalance: SubaccountEffectiveCorrespondingBalance = { available: new BigNumber(0), locked: new BigNumber(0) };

    if (subaccount.type === SubaccountType.FUTURES && props.currency.symbol === CURRENCY_ELIGIBLE_FOR_MARGIN) {
      const correspondingMarginBalance = subaccountMarginService.getAccountBalance(subaccount.accountId);

      if (correspondingMarginBalance) {
        effectiveCorrespondingBalance = {
          available: new BigNumber(correspondingMarginBalance.available),
          locked: new BigNumber(0),
          used: new BigNumber(correspondingMarginBalance.used),
        };
      }
    } else if (correspondingBalance) {
      effectiveCorrespondingBalance = correspondingBalance;
    }
    const precision = transactionPrecision.value;
    const disabled = subaccount.type === SubaccountType.FUTURES && props.currency.symbol !== CURRENCY_ELIGIBLE_FOR_MARGIN;

    return {
      displayValue: subaccount.name,
      displayRight: getBalance({ v: effectiveCorrespondingBalance.available, precision }),
      displayRightSymbol: props.currency.symbol,
      type: subaccount.type,
      name: subaccount.name,
      accountId: subaccount.accountId,
      correspondingBalance: {
        available: effectiveCorrespondingBalance.available,
        locked: effectiveCorrespondingBalance.locked,
        precision,
        ...('used' in effectiveCorrespondingBalance && { used: effectiveCorrespondingBalance.used }),
      },
      disabled,
    };
  }),
);
const fromSubaccount = ref(subaccountOptions.value.find((item) => item.name === preselection));
const toSubaccount = ref(
  props.preselectedTo
    ? subaccountOptions.value.find((item) => item.name === props.preselectedTo)
    : subaccountOptions.value.find((item) => item.name !== preselection && item.type !== SubaccountType.INSTANT_TRADE),
);
const shouldTargetOptionBeDisabled = (option: SubaccountOption) => {
  if (option.disabled) {
    return true;
  }

  const sourceIsInstantTrade = isInstant(fromSubaccount.value);
  const sourceIsMain = isMain(fromSubaccount.value);

  let disabled = false;

  if (sourceIsInstantTrade) {
    disabled = true;

    if (isMain(option)) {
      disabled = false;
    }
  } else if (sourceIsMain) {
    disabled = false;
  } else if (fromSubaccount.value?.name === option.name) {
    disabled = true;
  } else {
    // source is sub-account
    disabled = false;

    if (isInstant(option)) {
      disabled = true;
    }
  }

  return disabled;
};

const subaccountOptionsTarget = computed(() => {
  const options = [...subaccountOptions.value].map((option) => ({
    ...option,
    disabled: shouldTargetOptionBeDisabled(option),
  }));

  return options;
});

const isOptionTargetSelectable = (option: SubaccountOption) => !option.disabled;
const fromMaxAmount = computed(() => fromSubaccount.value?.correspondingBalance.available ?? new BigNumber(0));
const fromSubaccountId = computed(() => fromSubaccount.value?.accountId ?? nanoid());

const amountIsGreaterThanAvailableCoverBackend = computed(() => {
  if (fromSubaccount.value && fromSubaccount.value.name === CONSTANTS.MAIN_SUBACCOUNT_NAME) {
    return new BigNumber(amountValue.value).isGreaterThan(fromMaxAmount.value.minus(pendingAmount.value));
  }

  return false;
});

const getMain = () => subaccountOptions.value.find((e) => e.name === CONSTANTS.MAIN_SUBACCOUNT_NAME);

watch(fromSubaccountId, () => {
  amountValue.value = '';
  updateAmountIsValid(true);
});

watch(
  subaccountOptions,
  () => {
    const findUpdatedValue = (accountId?: string) => subaccountOptions.value.find((item) => item.accountId === accountId);
    const fromSubaccountOption = findUpdatedValue(fromSubaccount.value?.accountId);
    const toSubaccountOption = findUpdatedValue(toSubaccount.value?.accountId);

    fromSubaccount.value = !fromSubaccountOption || fromSubaccountOption.disabled ? getMain() : fromSubaccountOption;
    toSubaccount.value = !toSubaccountOption || toSubaccountOption.disabled ? getMain() : toSubaccountOption;
  },
  {
    deep: true,
    immediate: true,
  },
);

const onFromSubaccountUpdate = () => {
  if (isInstant(fromSubaccount.value) && !isMain(toSubaccount.value)) {
    toSubaccount.value = getMain();
  }

  if (!isMain(fromSubaccount.value) && isInstant(toSubaccount.value)) {
    toSubaccount.value = getMain();
  }
};
const onToSubaccountUpdate = () => {
  if (isInstant(toSubaccount.value) && !isMain(fromSubaccount.value)) {
    fromSubaccount.value = getMain();
  }

  if (!isMain(toSubaccount.value) && isInstant(fromSubaccount.value)) {
    fromSubaccount.value = getMain();
  }
};

const getLockedFunds = (locked?: BigNumber, precision?: number) => {
  if (precision === undefined || !locked || locked.eq(0)) {
    return undefined;
  }

  return getBalance({ v: locked, precision });
};

const getUsedForMarginFunds = (used?: BigNumber, precision?: number) => {
  if (precision === undefined || !used || used.eq(0)) {
    return undefined;
  }

  return getBalance({ v: used, precision });
};

const fromLockedFunds = computed(() => {
  const precision = fromSubaccount.value?.correspondingBalance.precision;
  const locked = fromSubaccount.value?.correspondingBalance.locked;

  return getLockedFunds(locked ? new BigNumber(locked) : undefined, precision);
});
const fromUsedForMarginFunds = computed(() => {
  const precision = fromSubaccount.value?.correspondingBalance.precision;
  const used = fromSubaccount.value?.correspondingBalance.used;

  return getUsedForMarginFunds(used ? new BigNumber(used) : undefined, precision);
});
const toLockedFunds = computed(() => {
  const precision = toSubaccount.value?.correspondingBalance.precision;
  const locked = toSubaccount.value?.correspondingBalance.locked;

  return getLockedFunds(locked ? new BigNumber(locked) : undefined, precision);
});
const toUsedForMarginFunds = computed(() => {
  if (!toSubaccount.value || !('used' in toSubaccount.value.correspondingBalance)) {
    return undefined;
  }

  const precision = toSubaccount.value?.correspondingBalance.precision;
  const used = toSubaccount.value?.correspondingBalance.used;

  return getUsedForMarginFunds(used ? new BigNumber(used) : undefined, precision);
});
const transferPrecision = computed(() => fromSubaccount.value?.correspondingBalance.precision ?? transactionPrecision.value);

const isValid = computed(() => amountIsValid.value && !!fromSubaccount.value && !!toSubaccount.value && fromSubaccount?.value?.displayValue !== toSubaccount?.value?.displayValue);

const transferError = ref<TransferFundsResponse['error']>();

const transferInternal = async (from: SubaccountOption, to: SubaccountOption) => {
  if (transferIsLoading.value || !'no-account-id') {
    return;
  }

  transferIsLoading.value = true;
  transferError.value = undefined;

  const { error } = await props.transfer({
    amount: amountValue.value,
    currencySymbol: props.currency.symbol,
    sourceId: from.accountId,
    sourceName: from.name,
    sourceType: from.type,
    destinationId: to.accountId,
    destinationName: to.name,
    destinationType: to.type,
  });

  transferIsLoading.value = false;
  transferError.value = error;
};

return (_ctx: any,_cache: any) => {
  const _component_ot_transition_fade = _resolveComponent("ot-transition-fade")!
  const _component_x_button = _resolveComponent("x-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(TransactionPaymentContent), { variant: __props.variant }, {
      default: _withCtx(() => [
        _createElementVNode("section", _hoisted_2, [
          (subaccountOptions.value)
            ? (_openBlock(), _createBlock(SubaccountSelect, {
                key: 0,
                value: fromSubaccount.value,
                "onUpdate:value": [
                  _cache[0] || (_cache[0] = ($event: any) => ((fromSubaccount).value = $event)),
                  onFromSubaccountUpdate
                ],
                class: "transfer-form__wallet-selector",
                name: "source",
                variant: __props.variant,
                options: subaccountOptions.value,
                label: _ctx.$t('modules.transactions.subaccountTransfer.transferFrom'),
                currency: __props.currency,
                disabled: __props.shouldDisableSelect,
                "locked-funds": fromLockedFunds.value,
                "used-for-margin-funds": fromUsedForMarginFunds.value,
                precision: transactionPrecision.value
              }, null, 8, ["value", "variant", "options", "label", "currency", "disabled", "locked-funds", "used-for-margin-funds", "precision"]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("section", _hoisted_3, [
          (_openBlock(), _createBlock(_unref(AmountInput), {
            key: fromSubaccountId.value,
            value: amountValue.value,
            "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((amountValue).value = $event)),
            variant: __props.variant,
            currency: __props.currency,
            precision: transferPrecision.value,
            "ignore-amount-too-small-check": true,
            "min-amount": 0,
            "max-amount": fromMaxAmount.value,
            "stop-validation": false,
            "translation-base-key": "modules.transactions.subaccountTransfer",
            onValidationChange: _cache[2] || (_cache[2] = ($event: any) => (updateAmountIsValid($event)))
          }, null, 8, ["value", "variant", "currency", "precision", "max-amount"]))
        ]),
        _createElementVNode("section", _hoisted_4, [
          (subaccountOptionsTarget.value)
            ? (_openBlock(), _createBlock(SubaccountSelect, {
                key: 0,
                value: toSubaccount.value,
                "onUpdate:value": [
                  _cache[3] || (_cache[3] = ($event: any) => ((toSubaccount).value = $event)),
                  onToSubaccountUpdate
                ],
                class: "transfer-form__wallet-selector",
                name: "target",
                placeholder: _ctx.$t('modules.transactions.subaccountTransfer.transferToPlaceholder'),
                variant: __props.variant,
                options: subaccountOptionsTarget.value,
                label: _ctx.$t('modules.transactions.subaccountTransfer.transferTo'),
                currency: __props.currency,
                disabled: __props.shouldDisableSelect,
                "locked-funds": toLockedFunds.value,
                "used-for-margin-funds": toUsedForMarginFunds.value,
                precision: transactionPrecision.value,
                selectable: isOptionTargetSelectable
              }, _createSlots({ _: 2 }, [
                (toSubaccount.value?.type === _unref(SubaccountType).FUTURES)
                  ? {
                      name: "subtext",
                      fn: _withCtx(() => [
                        _createElementVNode("div", _hoisted_5, [
                          _createElementVNode("p", null, _toDisplayString(_ctx.$t('modules.transactions.subaccountTransfer.transferToFuturesFootnote')), 1)
                        ])
                      ]),
                      key: "0"
                    }
                  : undefined
              ]), 1032, ["value", "placeholder", "variant", "options", "label", "currency", "disabled", "locked-funds", "used-for-margin-funds", "precision"]))
            : _createCommentVNode("", true),
          _createVNode(_component_ot_transition_fade, null, {
            default: _withCtx(() => [
              (transferError.value)
                ? (_openBlock(), _createBlock(_unref(ModalError), {
                    key: 0,
                    "is-code": false,
                    error: transferError.value
                  }, null, 8, ["error"]))
                : _createCommentVNode("", true),
              (amountIsGreaterThanAvailableCoverBackend.value)
                ? (_openBlock(), _createBlock(_unref(ModalError), {
                    key: 1,
                    "is-code": false,
                    "is-funds": false,
                    error: _unref(warningMessage)
                  }, null, 8, ["error"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }, 8, ["variant"]),
    _createVNode(_unref(ModalFooter), null, {
      default: _withCtx(() => [
        _createVNode(_component_x_button, {
          variant: "outline",
          text: _ctx.$t('modules.transactions.subaccountTransfer.buttons.cancel'),
          onClick: _cache[4] || (_cache[4] = ($event: any) => (emit('cancel')))
        }, null, 8, ["text"]),
        _createVNode(_component_x_button, {
          variant: "primary",
          disabled: !isValid.value || _unref(loadingPendingTransactions) || amountIsGreaterThanAvailableCoverBackend.value,
          loading: transferIsLoading.value,
          text: _ctx.$t('modules.transactions.subaccountTransfer.buttons.transfer'),
          onClick: _cache[5] || (_cache[5] = ($event: any) => (transferInternal(fromSubaccount.value, toSubaccount.value)))
        }, null, 8, ["disabled", "loading", "text"])
      ]),
      _: 1
    })
  ]))
}
}

})